import { connect, useSelector } from "react-redux";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import { ACUITY_INDEX } from "@/constants/acuity";
import {
  AcuityIndexTable,
  Dropdown,
  FormField,
  Image,
  Input,
  Link,
  LinkTypes,
  Text,
  TextTypes,
  Toggle,
} from "@/component-library";
import {
  addAppointment,
  checkEditUntouched,
  closeDrawer,
  editAppointment,
  fetchEventTypeOptions,
  resetAppointmentSelections,
  updateAppointmentEventType,
  updateAppointmentFrequencyDays,
  updateAppointmentFrequencyWeeks,
  updateAppointmentIsReoccurring,
  updateAppointmentLabel,
  updateAppointmentNumberOfOccurrences,
  updateAppointmentPatient,
} from "@/redux/actions/calendar";
import { AVAILABLE, AWAY, isSpecialEvent } from "@/constants/event";
import { Button2 } from "@/ui2/atoms/Button";
import { cn } from "@/lib/helpers/cn";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { isUserAdmin } from "@/utils/authToken";
import { selectIsSchedulingOnBehalfOf } from "@/redux/selectors/calendarSelectors";

import { CalendarAppointmentDateTimePicker } from "../AppointmentDateTimePicker/CalendarAppointmentDateTimePicker";
import {
  DISPLAY_DAYS,
  DISPLAY_WEEKS_MAP,
  numberOfOccurrencesArray,
  transformValueToDropDown,
} from "../../utils/appointmentPickerUtils";
import { getDefaultEventTypeByUserType } from "../../utils/general";

import { SelectableList } from "./SelectableList";

import "./styles.scss";

const CalendarAppointmentPicker = ({ className, menuPosition, ...props }) => {
  const patientDropdownOptions = props.calendar.patientDropdownOptions;
  const eventType = props.calendar.appointmentSelections.eventType;
  const reoccurring = props.calendar.appointmentSelections.seriesData.isReoccurring;
  const frequencyDays = props.calendar.appointmentSelections.seriesData.frequencyDays;
  const frequencyWeeks = props.calendar.appointmentSelections.seriesData.frequencyWeeks;
  const numberOfOccurrences = props.calendar.appointmentSelections.seriesData.numberOfOccurrences;
  const callId = props.calendar.appointmentSelections.callId; // If callId present appointment is being edited
  const errors = props.calendar.appointmentSelectionErrors;
  const providerName = props?.calendar?.provider?.name;
  const providerId = props?.calendar?.provider?.providerId;
  const acuityScore = props.calendar.appointmentSelections.acuityScore;
  const label = props.calendar.appointmentSelections?.label;

  const [showAcuityIndexTable, setShowAcuityIndexTable] = useState(false);

  const awayAppointmentTypeCondition = eventType && eventType === AWAY;
  const isUserAdminCondition = isUserAdmin();
  const availableAppointmentTypeCondition = eventType && eventType === AVAILABLE;
  const showInvitePatient = !awayAppointmentTypeCondition && !availableAppointmentTypeCondition;
  const showWorkingHourSuggestion = availableAppointmentTypeCondition;
  const isSchedulingOnBehalfOf = useSelector(selectIsSchedulingOnBehalfOf);

  useEffect(() => {
    props.checkEditUntouched();
  }, [props.calendar?.appointmentSelections]);

  useEffect(() => {
    if (!callId) {
      const defaultEventType = getDefaultEventTypeByUserType(
        props.calendar?.provider?.providerType,
      );

      props.updateAppointmentEventType(defaultEventType);
    }
  }, [props.calendar?.provider?.providerType]);

  const onAddClick = () => {
    if (callId) {
      const previousEvent = props.calendar.selectedEvent;

      props.editAppointment(previousEvent);
    } else {
      props.addAppointment();
    }
  };

  const onCancelClick = () => {
    props.resetAppointmentSelections();
    props.closeDrawer();
  };

  return (
    <div className={cn("apti-CalendarAppointmentPicker tw:mb-6 tw:px-6 tw:pb-6", className)}>
      <div>
        <FormField errorMessage={errors.eventType} label="Select Appointment Type">
          <Dropdown
            error={errors.eventType !== null}
            isDisabled={
              callId !== null && isSpecialEvent(props.calendar?.selectedEvent?.event_type)
            }
            menuPosition={menuPosition}
            name="appointmentType"
            onChange={(value) => {
              props.updateAppointmentEventType(value.target.value);
            }}
            options={props.calendar.eventTypeOptions}
            placeholder="Select Appointment Type"
            value={transformValueToDropDown(props.calendar.eventTypeOptions, eventType)}
          />
        </FormField>

        {awayAppointmentTypeCondition && (
          <FormField
            className=""
            label={
              <>
                Label <span className="tw:font-normal">(Optional)</span>
              </>
            }
          >
            <Input
              error={errors.label}
              name="awayLabel"
              onChange={(e) => props.updateAppointmentLabel(e.target.value)}
              placeholder="Out Of Office"
              value={label}
            />
          </FormField>
        )}

        {showInvitePatient && (
          <>
            <FormField errorMessage={errors.patientId} label="Invite Patient">
              <Dropdown
                error={errors.patientId !== null}
                isDisabled={callId !== null || isSchedulingOnBehalfOf}
                menuPlacement={window.innerWidth > 680 ? "bottom" : "top"}
                name="invitePatient"
                onChange={(value) => {
                  const patient = props.calendar.patientMap[value.target.value];

                  props.fetchEventTypeOptions(props.calendar.provider, patient);
                  props.updateAppointmentPatient(patient);
                }}
                options={patientDropdownOptions}
                placeholder="Invite Patient"
                value={transformValueToDropDown(
                  patientDropdownOptions,
                  props.calendar.appointmentSelections.patientId,
                )}
              />
            </FormField>

            {acuityScore && (
              <div className="apti-CalendarAppointmentPicker--acuityContainer tw:mt-2 tw:flex tw:w-full tw:flex-col tw:gap-2">
                <div
                  className="apti-CalendarAppointmentPicker--acuityText"
                  onClick={() => setShowAcuityIndexTable(!showAcuityIndexTable)}
                >
                  <span>
                    Acuity Index: {acuityScore} <span>{ACUITY_INDEX[acuityScore]?.label}</span>
                  </span>

                  <Image
                    alt="chevron"
                    className={`apti-CalendarAppointmentPicker--acuityChevron ${
                      showAcuityIndexTable && "chevron-flip"
                    }`}
                    src="icons/chevron_up.svg"
                  />
                </div>

                {showAcuityIndexTable && <AcuityIndexTable />}
              </div>
            )}
          </>
        )}

        <CalendarAppointmentDateTimePicker menuPosition={menuPosition} />
      </div>

      <div>
        <div className="d-flex py-2">
          <Toggle
            checked={reoccurring}
            className="mr-2"
            onClick={() => {
              props.updateAppointmentIsReoccurring(!reoccurring);
            }}
          />

          <Text className="apti-FormField--label d-flex align-items-center" type={TextTypes.label}>
            Reoccurring
          </Text>
        </div>

        {reoccurring && (
          <section className="apti-CalendarAppointmentPicker--recurring-container tw:mb-4 tw:flex tw:flex-col tw:gap-3 tw:p-3">
            <div className="apti-CalendarAppointmentPicker--wrapper-container">
              <FormField
                className="apti-CalendarAppointmentPicker--field apti-CalendarAppointmentPicker--wrapped-fields"
                label="Repeat Every"
              >
                <Dropdown
                  name="frequencyWeeks"
                  onChange={(value) => {
                    const frequencyWeeks = DISPLAY_WEEKS_MAP.find(
                      (o) => o.value === value.target.value,
                    );

                    props.updateAppointmentFrequencyWeeks(frequencyWeeks.value);
                  }}
                  options={DISPLAY_WEEKS_MAP}
                  placeholder="Repeat Every"
                  value={transformValueToDropDown(DISPLAY_WEEKS_MAP, frequencyWeeks)}
                />
              </FormField>

              <FormField
                className="apti-CalendarAppointmentPicker--field apti-CalendarAppointmentPicker--wrapped-fields"
                label="For"
              >
                <Dropdown
                  name="numberOfOccurrences"
                  onChange={(value) => {
                    const numberOfOccurrences = numberOfOccurrencesArray.find(
                      (o) => o.value === value.target.value,
                    );

                    props.updateAppointmentNumberOfOccurrences(numberOfOccurrences.value);
                  }}
                  options={numberOfOccurrencesArray}
                  placeholder="For"
                  value={transformValueToDropDown(numberOfOccurrencesArray, numberOfOccurrences)}
                />
              </FormField>
            </div>

            <div className="d-flex mx-0 w-100 flex-wrap justify-content-between" style={{ gap: 4 }}>
              <SelectableList
                currentlySelectedItems={frequencyDays}
                displayList={DISPLAY_DAYS}
                updateSelectedItems={(newState) => {
                  props.updateAppointmentFrequencyDays(newState);
                }}
              />
            </div>

            {errors.seriesData && <p className="fs-10 schedule-error">{errors.seriesData}</p>}
          </section>
        )}

        {showWorkingHourSuggestion && (
          <>
            {isUserAdminCondition && (
              <p className="apti-CalendarAppointmentPicker--available-text">
                Edit{" "}
                <Link href={`/app/profile/${providerId}`} type={LinkTypes.linkV2}>
                  working hours
                </Link>{" "}
                on {providerName}'s profile for permanently recurring availability slots.
              </p>
            )}

            {!isUserAdminCondition && (
              <p className="apti-CalendarAppointmentPicker--available-text">
                Edit{" "}
                <Link href="/app/profile" type={LinkTypes.linkV2}>
                  working hours
                </Link>{" "}
                on your profile for permanently recurring availability slots.
              </p>
            )}
          </>
        )}
      </div>

      <div className="tw:cols-2 tw:grid tw:grid-flow-col tw:gap-4">
        <Button2
          onPress={onCancelClick}
          variant="secondary"
          {...getAutomationDataAttr("calendar-appointment-picker-cancel-button")}
        >
          Cancel
        </Button2>

        <Button2
          onPress={onAddClick}
          {...getAutomationDataAttr("calendar-appointment-picker-confirm-button")}
        >
          {callId ? "Save" : "Add"}
        </Button2>
      </div>
    </div>
  );
};

CalendarAppointmentPicker.propTypes = {
  className: PropTypes.string,
  onAdd: PropTypes.func,
  onCancel: PropTypes.func,
};

CalendarAppointmentPicker.defaultProps = {
  className: undefined,
};

const mapStateToProps = (state) => {
  return {
    calendar: state.calendar,
  };
};

const mapDispatchToProps = {
  addAppointment,
  checkEditUntouched,
  closeDrawer,
  editAppointment,
  fetchEventTypeOptions,
  resetAppointmentSelections,
  updateAppointmentEventType,
  updateAppointmentFrequencyDays,
  updateAppointmentFrequencyWeeks,
  updateAppointmentIsReoccurring,
  updateAppointmentLabel,
  updateAppointmentNumberOfOccurrences,
  updateAppointmentPatient,
};

export default connect(mapStateToProps, mapDispatchToProps)(CalendarAppointmentPicker);
